/**
 * Currency utility for formatting amounts with correct currency symbols
 */

// Currency data - matches backend SUPPORTED_CURRENCIES
const CURRENCY_DATA = {
  USD: { symbol: '$', name: 'US Dollar', position: 'before' },
  EUR: { symbol: '€', name: 'Euro', position: 'before' },
  GBP: { symbol: '£', name: 'British Pound', position: 'before' },
  JPY: { symbol: '¥', name: 'Japanese Yen', position: 'before' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', position: 'before' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', position: 'before' },
  CHF: { symbol: 'Fr', name: 'Swiss Franc', position: 'before' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', position: 'before' },
  INR: { symbol: '₹', name: 'Indian Rupee', position: 'before' },
  MXN: { symbol: '$', name: 'Mexican Peso', position: 'before' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', position: 'before' },
  KRW: { symbol: '₩', name: 'South Korean Won', position: 'before' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', position: 'before' },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', position: 'before' },
  ZAR: { symbol: 'R', name: 'South African Rand', position: 'before' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', position: 'before' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', position: 'after' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', position: 'after' },
  DKK: { symbol: 'kr', name: 'Danish Krone', position: 'after' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', position: 'after' },
  // African currencies
  ETB: { symbol: 'Br', name: 'Ethiopian Birr', position: 'before' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', position: 'before' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', position: 'before' },
  EGP: { symbol: 'E£', name: 'Egyptian Pound', position: 'before' },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', position: 'before' },
  TZS: { symbol: 'TSh', name: 'Tanzanian Shilling', position: 'before' },
  UGX: { symbol: 'USh', name: 'Ugandan Shilling', position: 'before' },
  RWF: { symbol: 'FRw', name: 'Rwandan Franc', position: 'before' },
  MAD: { symbol: 'د.م.', name: 'Moroccan Dirham', position: 'after' },
  // Asian currencies
  PHP: { symbol: '₱', name: 'Philippine Peso', position: 'before' },
  THB: { symbol: '฿', name: 'Thai Baht', position: 'before' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', position: 'after' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', position: 'before' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', position: 'before' },
  PKR: { symbol: '₨', name: 'Pakistani Rupee', position: 'before' },
  BDT: { symbol: '৳', name: 'Bangladeshi Taka', position: 'before' },
  // Middle Eastern currencies
  SAR: { symbol: '﷼', name: 'Saudi Riyal', position: 'after' },
  QAR: { symbol: '﷼', name: 'Qatari Riyal', position: 'after' },
  KWD: { symbol: 'د.ك', name: 'Kuwaiti Dinar', position: 'before' },
  ILS: { symbol: '₪', name: 'Israeli Shekel', position: 'before' },
  TRY: { symbol: '₺', name: 'Turkish Lira', position: 'before' },
  // Latin American currencies
  ARS: { symbol: '$', name: 'Argentine Peso', position: 'before' },
  CLP: { symbol: '$', name: 'Chilean Peso', position: 'before' },
  COP: { symbol: '$', name: 'Colombian Peso', position: 'before' },
  PEN: { symbol: 'S/', name: 'Peruvian Sol', position: 'before' },
};

/**
 * Get currency data for a currency code
 */
export const getCurrencyData = (code) => {
  return CURRENCY_DATA[code] || { symbol: code, name: code, position: 'before' };
};

/**
 * Get just the currency symbol
 */
export const getCurrencySymbol = (code) => {
  return getCurrencyData(code).symbol;
};

/**
 * Format an amount with the correct currency symbol
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The currency code (e.g., 'ETB', 'USD')
 * @param {object} options - Formatting options
 */
export const formatCurrency = (amount, currencyCode = 'USD', options = {}) => {
  const {
    showCode = false,
    decimals = 2,
    showSign = false,
  } = options;

  const currency = getCurrencyData(currencyCode);
  const absAmount = Math.abs(amount);
  const formattedNumber = absAmount.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  let result;
  if (currency.position === 'after') {
    result = `${formattedNumber} ${currency.symbol}`;
  } else {
    result = `${currency.symbol}${formattedNumber}`;
  }

  if (showCode) {
    result = `${result} ${currencyCode}`;
  }

  if (showSign && amount !== 0) {
    result = amount < 0 ? `-${result}` : `+${result}`;
  } else if (amount < 0) {
    result = `-${result}`;
  }

  return result;
};

/**
 * Format amount for display in lists (with +/- sign based on type)
 */
export const formatTransactionAmount = (amount, type, currencyCode = 'USD') => {
  const isNegative = type === 'expense' || type === 'payment' || amount < 0;
  const displayAmount = Math.abs(amount);
  const formatted = formatCurrency(displayAmount, currencyCode);
  return isNegative ? `-${formatted}` : `+${formatted}`;
};

// Export CURRENCY_DATA for components to use in Select dropdowns
export { CURRENCY_DATA };

export default {
  getCurrencyData,
  getCurrencySymbol,
  formatCurrency,
  formatTransactionAmount,
  CURRENCY_DATA,
};
